import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { standardCommand } from '../../../core/model/command';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { RentalObjectCategory, RentalObjectCategoryToString, TenantCategory, TenantCategoryToString } from '../../model/const/enums';
import { ContactIdKind } from '../../model/contact';
import { RentApplication } from '../../model/rent-application';
import { RentApplicationContact } from '../../model/rent-application-contact';
import { ContactService } from '../../service/data/contact.service';
import { RentApplicationService } from '../../service/data/rent-application.service';

@Component({
    selector: 'app-rent-application-form',
    templateUrl: './rent-application-form.component.html',
    styleUrls: ['./rent-application-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: RentApplicationService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentApplicationFormComponent extends FormComponentBase<RentApplication> {
  constructor(
    private _fb: FormBuilder,
    private _cf: FormControlExFactory,
    private _contactService: ContactService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: RentApplicationService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  contacts: RentApplicationContact[];

  protected createModel(): RentApplication {
    return new RentApplication();
  }

  public rentalObjectCategories = [
    { name: RentalObjectCategoryToString(RentalObjectCategory.Dwelling), value: RentalObjectCategory.Dwelling },
    { name: RentalObjectCategoryToString(RentalObjectCategory.Parking), value: RentalObjectCategory.Parking },
    { name: RentalObjectCategoryToString(RentalObjectCategory.Premises), value: RentalObjectCategory.Premises },
    { name: RentalObjectCategoryToString(RentalObjectCategory.Other), value: RentalObjectCategory.Other },
  ];

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      rentalObjectCategory: this._cf.create('RENT_APPLICATION.OBJECT_CATEGORY', this.dataContext.rentalObjectCategory),
      contactCategory: this._cf.create('TENANT.CATEGORY', this.dataContext.contactCategory),
      contactCustomerNumber: this._cf.createReadonly('TENANT.CONTACT_NUMBER', this.dataContext.contactCustomerNumber),
      contactName: this._cf.createWithOptions('TENANT.NAME', this.dataContext.contactName, { length: 100, required: true }),
      applicationDate: this._fb.group({}),
      contactEmail: this._cf.createWithOptions('TENANT.EMAIL', this.dataContext.contactEmail, { length: 255, required: false }),
      contactPhone: this._cf.createWithOptions('TENANT.PHONE', this.dataContext.contactPhone, { length: 20, required: false }),
      idType: this._cf.create('TENANT.ID_TYPE.ID_TYPE', this.dataContext.idType),
      contactIdentification: this._cf.create('TENANT.ID_TYPE.ID_NUMBER', this.dataContext.contactIdentification),
      numberOfRoomsMin: this._cf.create('RENT_APPLICATION.MIN_ROOMS', this.dataContext.numberOfRoomsMin),
      numberOfRoomsMax: this._cf.create('RENT_APPLICATION.MAX_ROOMS', this.dataContext.numberOfRoomsMax),
      maxYearRent: this._cf.create('RENT_APPLICATION.MAX_YEAR_RENT', this.dataContext.maxYearRent),
      maxMonthRent: this._cf.create(
        'RENT_APPLICATION.MAX_MONTH_RENT',
        this.dataContext.maxYearRent == null ? null : this.dataContext.maxYearRent / 12
      ),
      comment: this._cf.createWithOptions('COMMON.NOTE', this.dataContext.comment, { length: 1000, required: false }),
      contactId: this._cf.createHidden(this.dataContext.contactId),
      contactIdExistingContact: this._cf.create('TENANT.EDIT_HEADER', this.dataContext.contactIdExistingContact),
    });
  }

  private _companyIdTypes: SelectionItem[] = [];
  private _personIdTypes: SelectionItem[] = [];
  get idTypes(): SelectionItem[] {
    if (!this.form) {
      return [];
    }
    return this.form.controls['contactCategory'].value === TenantCategory.Company ? this._companyIdTypes : this._personIdTypes;
  }

  get idType(): ContactIdKind {
    return this.form ? this.form.controls['idType'].value : null;
  }

  protected init(data: RentApplication) {
    super.init(data);
    this.form.controls['contactCategory'].updateValueAndValidity();
    this.form.controls['maxYearRent'].valueChanges.subscribe((value: number) => {
      let monthRent = value == null ? null : value / 12;
      if (this.form.controls['maxMonthRent'].value == monthRent) return;
      this.form.controls['maxMonthRent'].setValue(monthRent);
    });

    this.AddSubscription(
      this._contactService.getIdTypes().subscribe(types => {
        this._companyIdTypes = types.filter(t => t.category === TenantCategory.Company).map(t => new SelectionItem(t.caption, t.idType));
        this._personIdTypes = types
          .filter(t => t.category === TenantCategory.PhysicalPerson)
          .map(t => new SelectionItem(t.caption, t.idType));
        if (this.isNew) {
          this.form.controls['idType'].setValue(this.idTypes[0].value);
        }
      })
    );

    this.form.controls['maxMonthRent'].valueChanges.subscribe((value: number) => {
      let yearRent = value == null ? null : value * 12;
      if (this.form.controls['maxYearRent'].value == yearRent) return;
      this.form.controls['maxYearRent'].setValue(yearRent);
    });

    this.form.controls['contactIdExistingContact'].valueChanges.subscribe(value => {
      if (value instanceof RentApplicationContact) {
        this.form.controls['contactName'].setValue(value.contactName);
        this.form.controls['contactCategory'].setValue(value.contactCategory);
        this.form.controls['contactEmail'].setValue(value.contactEmail);
        this.form.controls['contactPhone'].setValue(value.contactPhone);
        this.form.controls['contactIdentification'].setValue(value.contactIdentification);
        this.form.controls['idType'].setValue(value.idType);
      } else {
        this.form.controls['contactName'].setValue(null);
        this.form.controls['contactEmail'].setValue(null);
        this.form.controls['contactPhone'].setValue(null);
        this.form.controls['contactIdentification'].setValue(null);
        this.form.controls['idType'].setValue(ContactIdKind.None);
        this.form.controls['contactCategory'].setValue(TenantCategory.PhysicalPerson);
      }
    });

    this._contactService.getRentApplicationContacts().subscribe(items => {
      this.contacts = items;
      const contact = this.contacts.find(x => x.id === this.dataContext.contactIdExistingContact);
      if (contact) {
        this.form.controls['contactIdExistingContact'].setValue(contact);
      } else if (this.dataContext.contactIdExistingContact) {
        this._contactService.getRentApplicationContact(this.dataContext.contactIdExistingContact).subscribe(item => {
          this.contacts.push(item);
          this.form.controls['contactIdExistingContact'].setValue(item);
        });
      }
    });
  }

  public get dwellingSelected(): boolean {
    return this.form.controls['rentalObjectCategory'].value == RentalObjectCategory.Dwelling;
  }

  contactCategories = [
    { name: TenantCategoryToString(TenantCategory.PhysicalPerson), value: TenantCategory.PhysicalPerson },
    { name: TenantCategoryToString(TenantCategory.Company), value: TenantCategory.Company },
  ];
}
